/** Footer: copy deck §9. Dark slate-900 4-column footer (PhishFence shell):
 * wordmark + tagline, three link columns with uppercase tracking-wide headers,
 * slate-400 → white hover links, and the compliance/legal note.
 *
 * Link status:
 *  - Product: Features/Deliverability/Pricing are real on-page anchors;
 *    Changelog is a "#" stub.
 *  - Company (About / Build log / Contact): all "#" stubs.
 *  - Legal & trust (Privacy / Terms / Anti-Spam / Acceptable Use / Cookie /
 *    Unsubscribe): all "#" stubs that MUST be live before launch (see deck
 *    §9 compliance note).
 */
import { Wordmark } from './Section.js';

interface FooterLink {
  label: string;
  href: string;
  /** True when href is a placeholder, not a real destination. */
  stub?: boolean;
}

const COLUMNS: { title: string; links: FooterLink[] }[] = [
  {
    title: 'Product',
    links: [
      { label: 'Features', href: '#features' },
      { label: 'Deliverability', href: '#deliverability' },
      { label: 'Pricing', href: '#pricing' },
      { label: 'Changelog', href: '#', stub: true },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', href: '#', stub: true },
      { label: 'Build log / blog', href: '#', stub: true },
      { label: 'Contact', href: '#', stub: true },
    ],
  },
  {
    title: 'Legal & trust',
    links: [
      { label: 'Privacy Policy', href: '#', stub: true },
      { label: 'Terms of Service', href: '#', stub: true },
      { label: 'Anti-Spam Policy', href: '#', stub: true },
      { label: 'Acceptable Use Policy', href: '#', stub: true },
      { label: 'Cookie Policy', href: '#', stub: true },
      { label: 'Unsubscribe / Opt-out', href: '#', stub: true },
    ],
  },
];

export function Footer() {
  return (
    <footer className="bg-slate-900 px-4 py-12 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Wordmark tone="dark" />
            <p className="mt-3 text-sm text-slate-400">
              Deliverability-first cold email.
            </p>
          </div>
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
                {col.title}
              </h3>
              <ul className="mt-3 space-y-2 text-sm">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-slate-400 transition-colors hover:text-white"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="mt-8 border-t border-slate-800 pt-6 text-xs leading-relaxed text-slate-400">
          RevBase is built for compliant, permission-respecting B2B outreach.
          Users are responsible for complying with CAN-SPAM, GDPR, and applicable
          local law. Every send includes one-click unsubscribe.
        </p>
      </div>
    </footer>
  );
}
